import { A } from '@solidjs/router';
import { mergeProps, splitProps, Show } from 'solid-js';
import type { Component, JSX } from 'solid-js';
import { RingLoader } from '~/components/common/loaders';
import { cn } from '~/utils/classnames';

export type ButtonProps = {
  type?: 'submit' | 'reset' | 'button';
  href?: string;
  form?: string;
  variant?: 'outlined' | 'solid' | 'white' | 'text';
  color?: 'primary' | 'link' | 'black' | 'red';
  rounded?: 'md' | 'lg' | 'full';
  loading?: boolean;
  class?: string;
  classList?: Record<string, boolean | undefined>;
  disabled?: boolean;
  children: JSX.Element;
  onClick?: (ev?: MouseEvent) => void;
  id?: string;
};

export const Button: Component<ButtonProps> = (originProps) => {
  const props = mergeProps(
    {
      type: 'button',
      color: 'primary',
      variant: 'solid',
      rounded: 'lg',
    } as ButtonProps,
    originProps
  );
  const [, tagProps] = splitProps(props, ['type', 'href', 'form', 'variant', 'color', 'rounded', 'loading', 'class']);

  const btnClass = () =>
    cn(
      'flex h-fit px-5 py-2.5 text-base leading-5',
      { border: props.variant !== 'text' },
      { 'border-primary-color': props.color === 'primary' },
      { 'border-border-level01': props.color === 'black' },
      { 'text-blue border-link [@media(hover:hover)]:hover:bg-blue/10': props.color === 'link' },
      { 'border-error text-error [@media(hover:hover)]:hover:bg-error/10': props.color === 'red' },
      {
        'text-[var(--primary-color)] [@media(hover:hover)]:hover:bg-[var(--hover-color)] [@media(hover:hover)]:hover:text-white':
          props.color === 'primary',
      },
      { '[@media(hover:hover)]:hover:text-white': props.color === 'primary' && props.variant === 'outlined' },
      { 'text-text-level01 [@media(hover:hover)]:hover:bg-text-level01/10': props.color === 'black' },
      { 'text-white': props.variant === 'solid' },
      {
        'bg-primary-color [@media(hover:hover)]:hover:bg-hover-color [@media(hover:hover)]:hover:text-white':
          props.variant === 'solid' && props.color === 'primary',
      },
      { 'bg-white [@media(hover:hover)]:hover:bg-white/90 [@media(hover:hover)]:hover:text-primary-color': props.variant === 'white' },
      { 'rounded-md': props.rounded === 'md' },
      { 'rounded-lg': props.rounded === 'lg' },
      { 'rounded-full': props.rounded === 'full' },
      { 'opacity-50 cursor-not-allowed': props.disabled },
      { 'aria-busy:bg-hover-color': props.variant === 'outlined' },
      props.class
    );

  const children = () => [
    <Show when={!props.loading} fallback={<RingLoader size={14} class="self-center" />}>
      <span class={cn('flex items-center justify-center gap-1')}>{props.children}</span>
    </Show>,
  ];

  return (
    <Show
      when={!props.href}
      fallback={
        <A href={props.href || ''} class={btnClass()} role="button" {...tagProps}>
          {children()}
        </A>
      }>
      <button
        id={props.id}
        class={btnClass()}
        type={props.type}
        form={props.form}
        {...tagProps}
        aria-busy={props.loading}
        onClick={(...args) => {
          if (props.disabled || props.loading) return;
          props.onClick?.(...args);
        }}>
        {children()}
      </button>
    </Show>
  );
};
